/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { tokens } from '@neo4j-ndl/base';
import { type EChartsOption, type SeriesOption } from 'echarts';
import { renderToString } from 'react-dom/server';

import {
  defaultThresholdLineSeriesOption,
  type EchartsSeries,
  type NeedleSeries,
  type ThresholdLineSeriesOption,
} from './Chart';
import { ChartTooltip } from './ChartTooltip';

const colors = Object.values(tokens.categorical);

export const mergeSeries = (
  series: NeedleSeries,
  theme: 'light' | 'dark' = 'light',
): EchartsSeries => {
  return series.map(
    (
      option:
        | SeriesOption
        | ThresholdLineSeriesOption<'warning'>
        | ThresholdLineSeriesOption<'danger'>,
      index: number = 0,
    ): SeriesOption => {
      if (option === undefined) {
        return {};
      }

      const color = Array.isArray(option.color)
        ? option.color[index]
        : (option.color ?? colors[index]);

      if (option.type === 'thresholdLine') {
        const { notificationType } = option;
        // Needed to differentiate between normal lines and threshold lines
        // as we are unable to inject custom properties into echarts series lines.
        const thresholdLineName = `thresholdLine-${notificationType}`;
        return {
          symbolSize: 9,
          symbol: 'none',
          condition: defaultThresholdLineSeriesOption.condition,
          ...option,
          lineStyle: {
            width: 2,
            type: 'dashed',
            ...option.lineStyle,
          },
          name: thresholdLineName,
          type: 'line',
          data: [
            [option.xAxis[0], option.yAxis],
            [option.xAxis[1], option.yAxis],
          ],
        };
      }

      if (option.type === 'line') {
        const shadow = tokens.theme[theme].boxShadow.raised;
        const shadowParts = shadow.split(/ (?=\d+px|rgba?\()/);

        return {
          type: 'line',
          symbolSize: 9,
          color,
          symbol: 'none',
          ...option,
          lineStyle: {
            width: 2,
            type: 'solid' as const,
            ...option.lineStyle,
          },
          emphasis: {
            disabled: true,
            focus: 'series',
            ...option.emphasis,
            itemStyle: {
              borderColor: tokens.theme[theme].color.neutral.bg.weak,
              shadowOffsetX: parseInt(shadowParts[0]),
              shadowOffsetY: parseInt(shadowParts[1]),
              shadowBlur: parseInt(shadowParts[2]),
              shadowColor: shadowParts[4],
              ...option.emphasis?.itemStyle,
            },
            label: { show: false },
            ...option.emphasis,
          },
        };
      }

      if (option.type === 'pie') {
        return {
          animationDuration: 600,
          animationType: 'scale',
          selectedMode: 'single',
          ...option,
          // Blur opacity is set to 1 to prevent other slices from
          // being opacitated when hovering a slice where the emphasis is enabled (used for scale effect)
          blur: {
            itemStyle: {
              opacity: 1,
              ...option.blur?.itemStyle,
            },
            label: {
              opacity: 1,
              ...option.blur?.label,
            },
            ...option.blur,
          },
          // Emphasis is enabled and used to apply the scale effect when hovering the pie chart slices.
          emphasis: {
            disabled: false,
            focus: 'self',
            scale: true,
            scaleSize: 5,
            ...option.emphasis,
          },
          itemStyle: {
            borderColor: tokens.theme[theme].color.neutral.bg.weak,
            borderWidth: 1,
            ...option.itemStyle,
          },
          label: {
            formatter: '{d}%',
            backgroundColor: 'transparent',
            color: tokens.theme[theme].color.neutral.text.weak,
            ...option.label,
          },
          labelLine: {
            length: 5,
            show: true,
            ...option.labelLine,
          },
          tooltip: {
            trigger: 'item',
            confine: true,
            // Reset the default tooltip css styling
            padding: 0,
            borderRadius: 0,
            borderWidth: 0,
            extraCssText: 'box-shadow: none; background-color: transparent;',
            formatter: function (params: unknown) {
              const paramsArray = Array.isArray(params) ? params : [params];
              const firstParam = paramsArray[0];
              let valueFormatter = (value: unknown) => `${value}`;

              return `${renderToString(
                <span className="ndl-charts-chart-tooltip">
                  <ChartTooltip.Content
                    key={firstParam?.value[0] ?? firstParam.seriesName}
                    leadingElement={
                      firstParam?.value[0] ?? firstParam.seriesName
                    }
                    trailingElement={valueFormatter(
                      Array.isArray(firstParam.value)
                        ? firstParam.value[1]
                        : firstParam.value,
                    )}
                    indentSquareColor={firstParam.color}
                  />
                </span>,
              )}`;
            },
            ...option.tooltip,
          },
        };
      }

      if (option.type === 'bar') {
        return {
          barMaxWidth: 16,
          ...option,
          blur: {
            itemStyle: {
              opacity: 0.3,
              ...option.blur?.itemStyle,
            },
            ...option.blur,
          },
          emphasis: {
            disabled: true,
            focus: 'series',
            ...option.emphasis,
          },
          itemStyle: {
            color,
            borderWidth: 20,
            ...option.itemStyle,
          },
          markLine: {
            lineStyle: {
              width: 20,
              ...option.markLine?.lineStyle,
            },
            emphasis: {
              disabled: true,
              lineStyle: {
                width: 20,
                ...option.markLine?.emphasis?.lineStyle,
              },
              ...option.markLine?.emphasis,
            },
          },
          tooltip: {
            trigger: 'axis',
            confine: true,
            // Reset the default tooltip css styling
            padding: 0,
            borderRadius: 0,
            borderWidth: 0,
            extraCssText: 'box-shadow: none; background-color: transparent;',
            formatter: function (params: unknown) {
              const paramsArray = Array.isArray(params) ? params : [params];
              const firstParam = paramsArray[0];
              let valueFormatter = (value: unknown) => `${value}`;

              return `${renderToString(
                <span className="ndl-charts-chart-tooltip">
                  <ChartTooltip.Title>{firstParam.name}</ChartTooltip.Title>
                  <ChartTooltip.Content
                    key={firstParam?.value[0] ?? firstParam.seriesName}
                    leadingElement={firstParam.seriesName}
                    trailingElement={valueFormatter(
                      Array.isArray(firstParam.value)
                        ? firstParam.value[1]
                        : firstParam.value,
                    )}
                    indentSquareColor={firstParam.color}
                  />
                </span>,
              )}`;
            },
            ...option.tooltip,
          },
        };
      }

      return option;
    },
  );
};

export const mergeXAxis = (
  xAxis: EChartsOption['xAxis'],
  theme: 'light' | 'dark' = 'light',
): EChartsOption['xAxis'] => {
  if (xAxis === undefined) {
    return [];
  }

  if (Array.isArray(xAxis)) {
    return xAxis.map((x) => mergeXAxis(x, theme)) as EChartsOption['xAxis'];
  }

  const baseXAxis: typeof xAxis = {
    ...xAxis,
    splitLine: { show: false, ...xAxis?.splitLine },
    axisLine: {
      lineStyle: {
        ...xAxis?.axisLine?.lineStyle,
      },
      ...xAxis?.axisLine,
    },
    axisTick: {
      ...xAxis?.axisTick,
      lineStyle: {
        ...xAxis?.axisTick?.lineStyle,
      },
      show: true,
      ...xAxis?.axisTick,
    },
    axisLabel: {
      margin: 16,
      formatter: {
        year: '{yearStyle|{yyyy}}\n{monthStyle|{MMM}}',
        month: '{MMM}',
        day: '{dd} {MMM}',
        hour: '{HH}:{mm}',
        minute: '{HH}:{mm}',
        second: '{HH}:{mm}:{ss}',
        millisecond: '{hh}:{mm}:{ss} {SSS}',
        // @ts-expect-error the type is not correct none is used when no other format works.
        none: '{yyyy}-{MM}-{dd} {hh}:{mm}:{ss} {SSS}',
      },
      overflow: 'truncate',
      ...xAxis?.axisLabel,
    },
  };

  if (xAxis.type === 'time') {
    const timeXAxis: typeof baseXAxis = {
      ...baseXAxis,
      axisLabel: {
        hideOverlap: true,
        ...baseXAxis.axisLabel,
      },
    };

    return timeXAxis;
  }

  if (xAxis.type === 'value' || xAxis.type === 'log') {
    const valueXAxis: typeof baseXAxis = {
      ...baseXAxis,
    };

    return valueXAxis;
  }

  if (xAxis.type === 'category') {
    const categoryXAxis: typeof baseXAxis = {
      ...baseXAxis,
      axisTick: {
        alignWithLabel: true,
        ...(Array.isArray(baseXAxis) ? {} : baseXAxis?.axisTick),
      },
      axisLabel: {
        width: 80,
        ...(Array.isArray(baseXAxis) ? {} : baseXAxis?.axisLabel),
      },
    };
    return categoryXAxis;
  }

  return baseXAxis;
};

export const mergeYAxis = (
  yAxis: EChartsOption['yAxis'],
  theme: 'light' | 'dark' = 'light',
): EChartsOption['yAxis'] => {
  if (yAxis === undefined) {
    return [];
  }

  if (Array.isArray(yAxis)) {
    return yAxis.map((y) => mergeYAxis(y, theme)) as EChartsOption['yAxis'];
  }

  const baseYAxis: typeof yAxis = {
    ...yAxis,
    axisLine: {
      ...yAxis?.axisLine,
      lineStyle: {
        ...yAxis?.axisLine?.lineStyle,
      },
      show: true,
    },
    axisTick: {
      show: false,
      ...yAxis?.axisTick,
      lineStyle: {
        width: 1,
        ...yAxis?.axisTick?.lineStyle,
      },
    },
    axisLabel: {
      margin: 8,
      overflow: 'truncate',
      ...yAxis?.axisLabel,
    },
    splitLine: { show: true, ...yAxis?.splitLine },
  };

  if (yAxis.type === 'time') {
    const timeYAxis: typeof baseYAxis = {
      ...baseYAxis,
      axisLabel: {
        hideOverlap: true,
        ...baseYAxis.axisLabel,
      },
      splitLine: { show: false, ...baseYAxis?.splitLine },
    };

    return timeYAxis;
  }

  if (yAxis.type === 'value' || yAxis.type === 'log') {
    const valueYAxis: typeof baseYAxis = {
      ...baseYAxis,
      axisLabel: {
        hideOverlap: true,
        ...baseYAxis.axisLabel,
      },
      splitLine: { show: false, ...baseYAxis?.splitLine },
    };

    return valueYAxis;
  }

  if (yAxis.type === 'category') {
    const categoryYAxis: typeof baseYAxis = {
      ...baseYAxis,
      axisLabel: {
        width: 100,
        ...(Array.isArray(baseYAxis) ? {} : baseYAxis?.axisLabel),
      },
    };

    return categoryYAxis;
  }

  return baseYAxis;
};

export const mergeDataZoom = (
  dataZoom: EChartsOption['dataZoom'],
): EChartsOption['dataZoom'] => {
  if (Array.isArray(dataZoom)) {
    return dataZoom.map((option) =>
      mergeDataZoom(option),
    ) as EChartsOption['dataZoom'];
  }

  const isSlider = dataZoom?.type === 'slider';
  const isInside = dataZoom?.type === 'inside';

  if (isSlider) {
    return {
      bottom: 10,
      height: 28,
      left: 50,
      right: 50,
      show: true,
      type: 'slider',
      ...dataZoom,
    };
  } else if (isInside) {
    return {
      filterMode: 'none',
      xAxisIndex: [0],
      zoomOnMouseWheel: false,
      ...dataZoom,
    };
  }

  return dataZoom;
};

export const mergeToolbox = (
  toolbox: EChartsOption['toolbox'],
): EChartsOption['toolbox'] => {
  if (Array.isArray(toolbox)) {
    return toolbox.map((option) =>
      mergeToolbox(option),
    ) as EChartsOption['toolbox'];
  }

  return {
    feature: {
      dataZoom: {
        yAxisIndex: false,
        ...toolbox?.feature?.dataZoom,
        show: true,
        icon: {
          // This hack removes the icons.
          // Due to: https://github.com/apache/echarts/issues/10274
          zoom: '-',
          back: '-',
        },
      },
    },
  };
};
